import React, { useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useT } from "~/lib/i18n";

export default function Login(): React.JSX.Element {
  const { t, locale } = useT();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "otp" | "verifying" | "error" | "otpError">("idle");
  const redirectTo = params.get("redirect") ?? "/scans";
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  async function onSubmitEmail(e: React.FormEvent) {
    e.preventDefault();
    setState("sending");
    try {
      const res = await fetch("/auth/request", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, redirectTo, locale }),
      });
      if (!res.ok) throw new Error(String(res.status));
      setState("otp");
    } catch {
      setState("error");
    }
  }

  async function onSubmitOtp(e: React.FormEvent) {
    e.preventDefault();
    if (code.length !== 6) return;
    setState("verifying");
    try {
      const res = await fetch("/auth/verify-otp", {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, code, redirectTo }),
      });
      if (!res.ok) {
        setState("otpError");
        return;
      }
      const data = await res.json();
      window.location.href = data.redirectTo ?? redirectTo;
    } catch {
      setState("otpError");
    }
  }

  async function onResend() {
    setCode("");
    setState("sending");
    try {
      const res = await fetch("/auth/request", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, redirectTo, locale }),
      });
      if (!res.ok) throw new Error(String(res.status));
      setState("otp");
    } catch {
      setState("error");
    }
  }

  function handleDigitChange(idx: number, value: string) {
    if (!/^\d*$/.test(value)) return;
    const digits = code.split("");
    digits[idx] = value.slice(-1);
    const next = digits.join("").slice(0, 6);
    setCode(next);
    if (value && idx < 5) {
      inputRefs.current[idx + 1]?.focus();
    }
  }

  function handleKeyDown(idx: number, e: React.KeyboardEvent) {
    if (e.key === "Backspace" && !code[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  }

  function handlePaste(e: React.ClipboardEvent) {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length > 0) {
      e.preventDefault();
      setCode(pasted);
      const focusIdx = Math.min(pasted.length, 5);
      inputRefs.current[focusIdx]?.focus();
    }
  }

  const showOtp = state === "otp" || state === "verifying" || state === "otpError";

  return (
    <main className="min-h-[calc(100vh-3rem)] flex items-center justify-center px-5">
      <div className="w-full max-w-[400px]">
        <span className="mono text-[11px] uppercase tracking-[0.12em] text-ink-tertiary">
          {t("common.signin")}
        </span>
        <h1 className="mt-2 font-display text-[28px] leading-[36px] text-ink-primary">
          {t("auth.signin.title")}
        </h1>
        <p className="mt-2 text-[13.5px] text-ink-secondary leading-[1.55]">
          {t("auth.signin.subtitle")}
        </p>

        {showOtp ? (
          <div className="mt-7">
            <p className="text-[14px] text-ink-primary font-medium">{t("auth.signin.sentTitle")}</p>
            <p className="mt-1 text-[12.5px] text-ink-secondary leading-[1.55]">
              {t("auth.signin.sentBody", { email })}
            </p>

            <form onSubmit={onSubmitOtp} className="mt-5 space-y-4">
              <label className="block">
                <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-ink-tertiary">
                  {t("auth.signin.otpLabel")}
                </span>
                <div className="mt-2 flex gap-2 justify-center" onPaste={handlePaste}>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <input
                      key={i}
                      ref={(el) => { inputRefs.current[i] = el; }}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      autoFocus={i === 0}
                      value={code[i] ?? ""}
                      onChange={(e) => handleDigitChange(i, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(i, e)}
                      className="w-11 h-12 text-center text-[20px] font-mono font-semibold rounded-[4px] border border-[var(--divider-strong)] bg-surface-card text-ink-primary outline-none focus:border-[var(--accent)] transition-colors duration-[var(--dur-quick)]"
                    />
                  ))}
                </div>
              </label>

              <button
                type="submit"
                disabled={code.length < 6 || state === "verifying"}
                className="w-full h-10 rounded-[4px] bg-[var(--accent)] text-[14px] font-medium text-[var(--ink-on-accent)] hover:bg-[var(--accent-hover)] disabled:opacity-60 transition-colors duration-[var(--dur-quick)]"
              >
                {state === "verifying" ? t("common.sending") : t("auth.signin.otpCta")}
              </button>

              {state === "otpError" && (
                <p className="text-[12.5px] text-deal-over text-center">{t("auth.signin.otpError")}</p>
              )}
            </form>

            <button
              type="button"
              onClick={onResend}
              className="mt-4 w-full text-center text-[12.5px] text-ink-secondary hover:text-ink-primary transition-colors"
            >
              {t("auth.signin.resend")}
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmitEmail} className="mt-7 space-y-3">
            <label className="block">
              <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-ink-tertiary">
                {t("auth.signin.emailLabel")}
              </span>
              <input
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("auth.signin.placeholder")}
                className="mt-1.5 w-full h-10 px-3 rounded-[4px] border border-[var(--divider-strong)] bg-surface-card text-[14px] text-ink-primary outline-none focus:border-[var(--accent)]"
              />
            </label>
            <button
              type="submit"
              disabled={state === "sending"}
              className="w-full h-10 rounded-[4px] bg-[var(--accent)] text-[14px] font-medium text-[var(--ink-on-accent)] hover:bg-[var(--accent-hover)] disabled:opacity-60 transition-colors duration-[var(--dur-quick)]"
            >
              {state === "sending" ? t("common.sending") : t("auth.signin.cta")}
            </button>
            {state === "error" && (
              <p className="text-[12.5px] text-deal-over">{t("auth.signin.error")}</p>
            )}
          </form>
        )}
      </div>
    </main>
  );
}
